import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';

import { cmsLegacyThemeConnector } from '../../PageBuilder/redux/connector';

const TabsContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const FadeOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 70px;
  pointer-events: none;
  z-index: 2;

  &.left {
    left: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }

  &.right {
    right: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }
`;

const TabsBar = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 767px) {
    justify-content: flex-start;
  }

  @media (min-width: 768px) {
    justify-content: center;

    &.has-overflow {
      justify-content: flex-start;
    }
  }
`;

const TabButton = cmsLegacyThemeConnector(styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  color: ${props => (props.active ? props.legacyTheme.colors.brand : '#666')};
  border: none;
  border-bottom: 2px solid
    ${props => {
      if (props.active) return props.legacyTheme.colors.brand;
      if (props.isCompleted) return props.legacyTheme.colors.brand;
      return '#e0e0e0';
    }};
  cursor: pointer;
  font-family: ${props => props.legacyTheme.fonts.body};
  font-weight: ${props => (props.active ? 'bold' : 'normal')};
  transition: all 0.3s ease;
  text-transform: uppercase;
  flex-shrink: 0;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    min-width: fit-content;
    gap: 12px;
  }

  @media (max-width: 767px) {
    padding: 12px 16px;
    max-width: 150px;
    gap: 6px;
  }
`);

const StepIndicator = cmsLegacyThemeConnector(styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
  transition: all 0.3s ease;

  ${props => {
    if (props.isCompleted && !props.active) {
      return `
        background: ${props.legacyTheme.colors.brand};
        color: white;
      `;
    }
    if (props.active) {
      return `
        background: ${props.legacyTheme.colors.brand};
        color: white;
      `;
    }
    return `
      background: #e0e0e0;
      color: #666;
    `;
  }}
`);

const CheckIcon = cmsLegacyThemeConnector(styled.span`
  font-size: 14px;
  font-weight: bold;
  color: ${props => props.legacyTheme.colors.brand};
`);

const TabsNavigation = ({ tabs = [], activeIndex = 0, onSelect }) => {
  const tabsBarRef = useRef(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollability = () => {
    if (tabsBarRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsBarRef.current;
      const hasOverflow = scrollWidth > clientWidth;

      setShowScrollButtons(hasOverflow);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    checkScrollability();

    const handleResize = () => checkScrollability();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [tabs]);
  let visibleStepCount = 0;
  return (
    <TabsContainer>
      {showScrollButtons && canScrollLeft && <FadeOverlay className="left" />}

      <TabsBar
        ref={tabsBarRef}
        role="tablist"
        className={showScrollButtons ? 'has-overflow' : ''}
        onScroll={checkScrollability}
      >
        {tabs.map(({ title, index, id, present }) => {
          const isCompleted = index < activeIndex;
          const isActive = activeIndex === index;
          if (!present) {
            return null;
          }
          visibleStepCount += 1;

          return (
            <TabButton
              key={title || id}
              type="button"
              role="tab"
              active={isActive}
              isCompleted={isCompleted}
              aria-selected={isActive}
              data-active={isActive}
              onClick={() => onSelect(index)}
            >
              {isCompleted ? (
                <CheckIcon className="oc-icon-check">✓</CheckIcon>
              ) : (
                <StepIndicator isCompleted={isCompleted} active={isActive}>
                  {visibleStepCount}
                </StepIndicator>
              )}
              {title || `Tab ${id}`}
            </TabButton>
          );
        })}
      </TabsBar>

      {showScrollButtons && canScrollRight && <FadeOverlay className="right" />}
    </TabsContainer>
  );
};

TabsNavigation.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      index: PropTypes.number,
      id: PropTypes.number,
    })
  ),
  activeIndex: PropTypes.number,
  onSelect: PropTypes.func,
};

TabsNavigation.defaultProps = {
  tabs: [],
  activeIndex: 0,
  onSelect: () => {},
};

export default TabsNavigation;
